import { useState, useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import api from "@/lib/api";
import {
  Globe, Search, ExternalLink, Key, Eye, EyeOff, Package,
  Loader2, Copy, CheckCircle, Monitor, Layers, ArrowRight
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function ProductDemosPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});
  const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
  const isInternal = userData.user_type !== "external" || userData.role === "admin";

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const r = await api.get("/presales/products/browse");
      setProducts(r.data || []);
    } catch {
      toast.error("Failed to load products");
    }
    setLoading(false);
  };

  const categories = ["all", ...new Set(products.map(p => p.category).filter(Boolean))];

  const filtered = products.filter(p => {
    const q = search.toLowerCase();
    const matchesSearch = !q || (p.name || "").toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q);
    const matchesCategory = selectedCategory === "all" || p.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      const t = document.createElement("textarea");
      t.value = text;
      t.style.cssText = "position:fixed;left:-9999px";
      document.body.appendChild(t);
      t.select();
      document.execCommand("copy");
      document.body.removeChild(t);
      toast.success(`${label} copied`);
    }
  };

  const togglePassword = (productId, role) => {
    setShowPasswords(prev => ({ ...prev, [`${productId}-${role}`]: !prev[`${productId}-${role}`] }));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="pt-20 md:pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">

          {/* Hero Section */}
          <div className="relative mb-8 overflow-hidden rounded-3xl bg-gradient-to-br from-[#0066CC] to-[#004C99] p-8 md:p-10">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-10 left-10 w-40 h-40 bg-white rounded-full blur-3xl" />
              <div className="absolute bottom-5 right-20 w-60 h-60 bg-white rounded-full blur-3xl" />
            </div>
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Monitor className="w-5 h-5 text-white" />
                </div>
                <span className="text-white/80 text-sm font-medium tracking-wide uppercase">Product Demo Repository</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" data-testid="product-demos-title">
                Explore Our Solutions
              </h1>
              <p className="text-white/70 text-sm md:text-base max-w-xl">
                Access live demos of ABSOLIN's ERP and digital transformation products.
                {isInternal ? " Internal credentials available for each product." : " Browse and experience our solutions firsthand."}
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products..." className="pl-9 bg-white" data-testid="product-search-input" />
            </div>
            {categories.length > 1 && (
              <div className="flex gap-1.5 overflow-x-auto pb-1">
                {categories.map(cat => (
                  <button key={cat} onClick={() => setSelectedCategory(cat)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${selectedCategory === cat ? "bg-[#0066CC] text-white shadow-sm" : "bg-white text-slate-600 border border-slate-200 hover:border-[#0066CC]/30"}`}
                    data-testid={`filter-${cat}`}>
                    {cat === "all" ? "All Products" : cat}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 animate-spin text-[#0066CC]/30" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
              <Package className="w-16 h-16 text-slate-200 mx-auto mb-4" />
              <p className="text-slate-400 text-sm mb-1">{search ? "No products match your search" : "No products available yet"}</p>
              <p className="text-slate-300 text-xs">Check back later for product demos</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map(p => (
                <div key={p.id} className="group bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg hover:border-[#0066CC]/20 transition-all duration-300 overflow-hidden"
                  data-testid={`product-card-${p.id}`}>
                  {/* Card Header */}
                  <div className="p-5 pb-3">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0066CC]/10 to-[#0066CC]/5 border border-[#0066CC]/10 flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                        <Layers className="w-6 h-6 text-[#0066CC]" />
                      </div>
                      {p.category && (
                        <span className="px-2.5 py-1 bg-slate-100 text-slate-500 rounded-full text-xs font-medium">{p.category}</span>
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-1 group-hover:text-[#0066CC] transition-colors">{p.name}</h3>
                    {p.description && (
                      <p className="text-sm text-slate-500 leading-relaxed line-clamp-2">{p.description}</p>
                    )}
                  </div>

                  {/* Demo URL */}
                  {p.demo_url && (
                    <div className="px-5 pb-3">
                      <a href={p.demo_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0066CC] text-white rounded-xl text-sm font-medium hover:bg-[#0052A3] transition-colors w-full justify-center group/btn"
                        data-testid={`demo-link-${p.id}`}>
                        <ExternalLink className="w-4 h-4" />
                        Open Live Demo
                        <ArrowRight className="w-3.5 h-3.5 group-hover/btn:translate-x-0.5 transition-transform" />
                      </a>
                    </div>
                  )}

                  {/* Credentials (Internal Users Only) */}
                  {isInternal && p.credentials && Object.values(p.credentials).some(c => c?.username) && (
                    <div className="border-t border-slate-100">
                      <button onClick={() => setExpandedProduct(expandedProduct === p.id ? null : p.id)}
                        className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50/50 transition-colors"
                        data-testid={`toggle-creds-${p.id}`}>
                        <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-500">
                          <Key className="w-3.5 h-3.5" /> Demo Credentials
                        </span>
                        <span className="text-xs text-slate-400">{expandedProduct === p.id ? "Hide" : "Show"}</span>
                      </button>

                      {expandedProduct === p.id && (
                        <div className="px-5 pb-4 space-y-2">
                          {["admin", "user", "other"].map(role => {
                            const cred = p.credentials[role];
                            if (!cred?.username) return null;
                            const isVisible = showPasswords[`${p.id}-${role}`];
                            return (
                              <div key={role} className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                                <div className="flex items-center justify-between mb-1.5">
                                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{role}</span>
                                  <div className="flex gap-1">
                                    <button onClick={() => togglePassword(p.id, role)} className="p-1 hover:bg-white rounded transition-colors">
                                      {isVisible ? <EyeOff className="w-3 h-3 text-slate-400" /> : <Eye className="w-3 h-3 text-slate-400" />}
                                    </button>
                                    <button onClick={() => copyToClipboard(`Username: ${cred.username}\nPassword: ${cred.password}`, `${role} credentials`)}
                                      className="p-1 hover:bg-white rounded transition-colors">
                                      <Copy className="w-3 h-3 text-slate-400" />
                                    </button>
                                  </div>
                                </div>
                                <div className="space-y-0.5">
                                  <p className="text-xs"><span className="text-slate-400">User:</span> <span className="font-mono font-medium text-slate-700">{cred.username}</span></p>
                                  <p className="text-xs"><span className="text-slate-400">Pass:</span> <span className="font-mono font-medium text-slate-700">{isVisible ? cred.password : "••••••••"}</span></p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* No URL fallback */}
                  {!p.demo_url && (
                    <div className="px-5 pb-4">
                      <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 text-slate-400 rounded-xl text-sm border border-dashed border-slate-200 justify-center">
                        <Globe className="w-4 h-4" />
                        Demo URL pending
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Footer note */}
          {!loading && filtered.length > 0 && (
            <div className="mt-8 text-center">
              <p className="text-xs text-slate-400">
                Showing {filtered.length} product{filtered.length !== 1 ? "s" : ""}
                {isInternal && " · Credentials visible to internal users only"}
              </p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
